import {
  FunctionComponent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useQuery } from '@tanstack/react-query'
import styled, { css } from 'styled-components'

import config from '@config'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import {
  getAureusBatchRecommendations,
  GetAureusBatchRecommendationsResponse,
  triggerAureusDeboosting,
} from '@utils/aureus'
import { Link } from '@utils/cook/types'
import { WidgetKind } from '@utils/registry'
import InvertedColorsContext from '@contexts/invertedColors'
import useHasAureusJsClientScriptInitialized from '@hooks/useHasAureusJsClientScriptInitialized'
import usePageMetadata from '@hooks/usePageMetadata'
import useViewportType from '@hooks/useViewport/useViewportType'
import { TeaserStandardVerticalVideoAPIProps } from '@widgets/TeaserStandardVerticalVideo'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import JSONRenderer from '@components/JSONRenderer'
import SectionHeader from '@components/SectionHeader'
import Swiper, { ArrowsVisibilityType, SwiperClass } from '@components/Swiper'
import NavigationArrows from '@components/Swiper/NavigationArrows'

const {
  backend: { deploymentEnv },
} = config

type VideosCarouselItem = TeaserStandardVerticalVideoAPIProps & {
  kind: WidgetKind
}

export interface VideosCarouselWidgetAPIProps {
  title: string
  accentColor: string
  items: VideosCarouselItem[]
  link?: Link
}

interface FadeGradientProps {
  isHidden: boolean
  itemsCount: number
  direction: 'left' | 'right'
}

const VideosCarouselWrapper = styled.div`
  ${({
    theme: {
      colors: { staticBlack },
      spacing: { spacing16, spacing32, spacing56 },
      measures: { stretchBackground },
    },
  }) => css`
    background-color: ${staticBlack};

    ${stretchBackground()};

    padding-top: ${spacing32};
    padding-bottom: ${spacing32};

    display: grid;
    grid-row-gap: ${spacing16};

    ${desktopCSS(css`
      padding: ${spacing56};
      grid-row-gap: ${spacing32};
    `)}
  `}
`

const StyledNavigationArrows = styled(NavigationArrows)<{ itemsCount: number }>`
  ${({ itemsCount }) => css`
    ${itemsCount <= 2 &&
    mobileCSS(css`
      display: none;
    `)}
    ${itemsCount <= 4 &&
    tabletCSS(css`
      display: none;
    `)};
    ${itemsCount <= 6 &&
    desktopCSS(css`
      display: none;
    `)}
  `}
`

const SwiperWrapper = styled.div`
  ${({
    theme: {
      measures: { outerPadding },
    },
  }) => css`
    position: relative;
    overflow: hidden;

    ${mobileCSS(css`
      margin-right: -${outerPadding.mobile};
    `)}
  `}
`

const StyledSwiper = styled(Swiper)`
  ${({
    theme: {
      spacing: { spacing16, spacing24 },
    },
  }) => css`
    > .swiper-items-container {
      grid-gap: ${spacing16};
      grid-template-rows: repeat(3, minmax(0, 1fr));

      ${desktopCSS(css`
        grid-gap: ${spacing24};
        grid-template-rows: repeat(2, minmax(0, 1fr));
      `)}
    }
  `}
`

const SwiperSlide = styled.div`
  ${({
    theme: {
      measures: { outerPadding },
    },
  }) => css`
    width: 227px;

    ${mobileCSS(css`
      &:last-child {
        margin-right: ${outerPadding.mobile};
        scroll-margin-right: ${outerPadding.mobile};
      }
    `)}
  `}
`

const FadeGradient = styled.div<FadeGradientProps>`
  ${({
    theme: {
      colors: { staticBlack },
      utils: { opacity },
    },
    isHidden,
    itemsCount,
    direction,
  }) => css`
    width: 48px;
    height: 100%;

    position: absolute;
    z-index: 1;
    top: 0;

    pointer-events: none;

    ${direction}: 0;
    background: linear-gradient(
      to ${direction},
      ${opacity(staticBlack, '00')},
      ${staticBlack}
    );

    ${isHidden &&
    css`
      opacity: 0;
    `};

    ${desktopCSS(css`
      width: 80px;
    `)};

    ${itemsCount <= 2 &&
    mobileCSS(css`
      display: none;
    `)};

    ${itemsCount <= 4 &&
    tabletCSS(css`
      display: none;
    `)};

    ${itemsCount <= 6 &&
    desktopCSS(css`
      display: none;
    `)}
  `}
`

interface AureusVideoCarouselItem {
  aureusOfferId: string
  articleID: string
  teasers: {
    standardVerticalVideo: VideosCarouselItem
  }
}

type GetAureusVideoCarouselItems = (
  sectionName: 'home' | 'sport',
  contentBlacklist?: string[]
) => GetAureusBatchRecommendationsResponse<AureusVideoCarouselItem>

const getAureusVideoCarouselItems: GetAureusVideoCarouselItems = (
  sectionName,
  contentBlacklist
) =>
  getAureusBatchRecommendations<AureusVideoCarouselItem>({
    'polaris-viral-videos': {
      resultsLimit: 12,
      context: {
        section: `polaris-viral-videos-${sectionName === 'sport' ? 'sport-' : ''}${deploymentEnv === 'prod' ? 'prod' : 'stg'}`,
        purpose: 'reference',
        minHardness: 3,
        contentBlacklist,
      },
    },
  } as const)

const getVideoCarouselItems = async (
  sectionName: 'home' | 'sport',
  blacklist?: string[]
) => {
  let responseData: VideosCarouselItem[] = []

  try {
    const queryResult = await getAureusVideoCarouselItems(
      sectionName,
      blacklist
    )

    responseData =
      queryResult?.results?.['polaris-viral-videos']?.recommendations?.reduce(
        (acc, curr) => {
          acc.push({
            ...curr.teasers.standardVerticalVideo,
            articleId: curr.articleID,
            aureusOfferId: curr.aureusOfferId,
          } satisfies VideosCarouselItem)

          return acc
        },
        [] as VideosCarouselItem[]
      ) ?? []
  } catch {}

  return responseData
}

const sendDeboostingEvent = (articleId: string, aureusOfferId?: string) => {
  if (aureusOfferId) {
    triggerAureusDeboosting({
      itemsToDeboost: [{ offerId: aureusOfferId, contentId: articleId }],
    })
  }
}

const SLIDES_PER_GROUP_MOBILE = 3
const SLIDES_PER_GROUP_DESKTOP = 8

const VideosCarousel: FunctionComponent<VideosCarouselWidgetAPIProps> = ({
  title,
  accentColor,
  link,
  items,
}) => {
  const viewportType = useViewportType()
  const isDesktop = viewportType === 'desktop'

  const hasAureusJsClientScriptInitialized =
    useHasAureusJsClientScriptInitialized()

  const { contextId, personalizationInfo } = usePageMetadata()

  const swiperRef = useRef<SwiperClass | null>(null)
  const [arrowsVisibility, setArrowsVisibility] =
    useState<ArrowsVisibilityType>('next')

  const shouldHideLeftGradient = useMemo(
    () => ['none', 'next', 'both'].includes(arrowsVisibility || ''),
    [arrowsVisibility]
  )

  const shouldHideRightGradient = useMemo(
    () => ['none', 'prev'].includes(arrowsVisibility || ''),
    [arrowsVisibility]
  )

  const onInit = useCallback(
    (swiper: any) => {
      swiperRef.current = swiper
    },
    [swiperRef]
  )

  const onArrowsVisibility = useCallback((arrow: ArrowsVisibilityType) => {
    setArrowsVisibility(arrow)
  }, [])

  const sectionName = useMemo(
    () => (contextId === '/sport/' ? 'sport' : 'home'),
    [contextId]
  )

  const { data: videoCarouselItems } = useQuery<VideosCarouselItem[]>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['video-carousel-items', sectionName],
    queryFn: () =>
      getVideoCarouselItems(sectionName, personalizationInfo?.contentBlacklist),
    enabled: hasAureusJsClientScriptInitialized,
    gcTime: 0,
  })

  const itemsToDisplay = useMemo(() => {
    if (!videoCarouselItems) {
      return items
    }
    return [...videoCarouselItems, ...items].slice(0, 12)
  }, [items, videoCarouselItems])

  if (!title) {
    return null
  }

  return (
    <InvertedColorsContext.Provider value={true}>
      <VideosCarouselWrapper>
        <SectionHeader
          title={title}
          link={link}
          accentColor={accentColor}
          arrowsVisibility={arrowsVisibility}>
          <StyledNavigationArrows
            swiperRef={swiperRef}
            arrowsVisibility={arrowsVisibility}
            itemsCount={itemsToDisplay?.length ?? 0}
          />
        </SectionHeader>
        <SwiperWrapper>
          <StyledSwiper
            initialSlide={0}
            slidesPerGroup={
              isDesktop ? SLIDES_PER_GROUP_DESKTOP : SLIDES_PER_GROUP_MOBILE
            }
            spaceTopBottom={0}
            onInit={onInit}
            onArrowsVisibility={onArrowsVisibility}
            slides={itemsToDisplay?.map((item) => (
              <SwiperSlide
                key={item.articleId}
                onClick={() =>
                  sendDeboostingEvent(item.articleId, item.aureusOfferId)
                }>
                <JSONRenderer>{item}</JSONRenderer>
              </SwiperSlide>
            ))}
          />
          <FadeGradient
            isHidden={shouldHideLeftGradient && shouldHideRightGradient}
            direction={shouldHideLeftGradient ? 'right' : 'left'}
            itemsCount={itemsToDisplay?.length ?? 0}
          />
        </SwiperWrapper>
      </VideosCarouselWrapper>
    </InvertedColorsContext.Provider>
  )
}

const widget = {
  kind: ['videos-carousel'],
  component: VideosCarousel,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
